import React, { useRef, useEffect, useCallback, useMemo, Dispatch, SetStateAction } from 'react'
import { a, useSpring } from '@react-spring/web'
import { useDrag, useInputContext, debounce, colord, useTh, Components, mergeRefs } from 'leva/plugin'
import { Canvas, SpringPreview } from '../styles/StyledSpring'
import type { BounceProps, CubicBounceLerp } from '../types/bounce-types'
import { useCanvas2d } from '../utilities/useCanvas'
import { StyledBounce } from '../styles/StyledBounce'
import { BouncePreview } from './BouncePreview'
import { displayGridIncrement, elasticSegmentSearchMax } from '../assets/constants'
import { ElasticPreview } from './ElasticPreview'
import { rangeMap } from '../utilities/bezier-utils'
const { Row } = Components

const SpringPreviewAnimated = a(SpringPreview)

export function ElasticCanvas(props: { setBounceValues: Dispatch<SetStateAction<any[]>> }) {
  const { displayValue, value, onUpdate, settings } = useInputContext<BounceProps>()
  const springRef = useRef(displayValue)
  const accentColor = useTh('colors', 'highlight3')
  const backgroundColor = useTh('colors', 'elevation2')
  const fillColor = useTh('colors', 'highlight1')
  const [displayWidth, setDisplayWidth] = React.useState(0)
  const [displayHeight, setDisplayHeight] = React.useState(0)
  const [ newBounces, setNewBounces ] = React.useState<CubicBounceLerp[]>([])
  const [gradientTop, gradientBottom] = useMemo(() => {
    return [colord(fillColor).alpha(0.4).toRgbString(), colord(fillColor).alpha(0.1).toRgbString()]
  }, [fillColor])

  const { elasticity, gravity, hang, velocity, decayY, decayX  } = displayValue
  const { elasticity: ts, gravity: fs } = settings!

  const [spring, api] = useSpring(() => ({
    scaleX: 0.5,
    opacity: 0.2,
    immediate: (k) => k === 'opacity',
  }))

  const bind = useDrag(({ movement: [x, y], memo = [elasticity, gravity] }) => {
    onUpdate({
      ...value,
      elasticity: memo[0] - Math.round(x) * ts.step,
      gravity: memo[1] - Math.round(y / 4) * fs.step,
    })
    return memo
  })

  //@ts-ignore
  function scale (number, inMin, inMax, outMin, outMax) {
    return (number - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
  }

  //@ts-ignore
  const createHalfBounce = function(ognHandle, ogn, dstHandle, dst, dims, pathObj) {
        let { ognX, ognY } = ogn
        let { ognHandleX, ognHandleY } = ognHandle
        let { dstX, dstY } = dst
        let { dstHandleX, dstHandleY } = dstHandle
        pathObj.moveTo(ognX, ognY)
        pathObj.bezierCurveTo(ognHandleX, ognHandleY, dstHandleX, dstHandleY, dstX, dstY)
        pathObj.moveTo(dstX, dstY)
        let arcXLength = (dstX - ognX) * 2
        let originHandleXDifference = ognX - ognHandleX
        let dstHandleXDifference = dstX - dstHandleX
        // let mirroredOgnHandleX = dstX + originHandleXDifference  
        // let mirroredPeakHandleX = dstX + dstHandleXDifference
        // pathObj.bezierCurveTo(mirroredPeakHandleX, dstHandleY, mirroredOgnHandleX, ognHandleY, dstX, ognY)


        let ognXCubic = scale(ognHandleX, ognX, dstX, 0, 1)
        let ognYCubic = scale(ognHandleY, ognY, dstY, 0, 1)
        let dstXCubic = scale(dstHandleX, ognX, dstX, 0, 1)
        let dstYCubic = scale(dstHandleY, ognY, dstY, 0, 1)
        let ognLerpY = scale(ognY, 0, dims.height, 0, 1)
        let ognLerpX = scale(ognX, 0, dims.width, 0, 1)
        let dstLerpY = scale(dstY, 0, dims.height, 0, 1)
        let dstLerpX = scale(dstX, 0, dims.width, 0, 1)

        let cubicBounceLerp = {
          ognXCubic: 1 - ognXCubic,
          ognYCubic: 1 - ognYCubic,
          dstXCubic,
          dstYCubic,
          mirrorPeakXCubic: 1 - ognYCubic,
          mirrorPeakYCubic: 1 - ognXCubic,
          ognLerpX,
          ognLerpY,
          dstLerpY,
          dstLerpX
        }

        return cubicBounceLerp
  }

  // const updateSpring = useMemo(
  //   () =>
  //     debounce(() => {
  //       const { elasticity, gravity, hang, velocity, decayY, decayX  } = springRef.current
  //       api.start({
  //         //@ts-ignore
  //         from: { scaleX: 0, opacity: 0.9 },
  //         to: [{ scaleX: 0.5 }, { opacity: 0.2 }],
  //         config: { elasticity, gravity, hang, velocity, decayY, decayX  },
  //       })
  //     }, 250),
  //   [api]
  // )


 
const drawSpring = useCallback(
  (_canvas: HTMLCanvasElement, _ctx: CanvasRenderingContext2D) => {
    let { elasticity, gravity, hang, velocity, decayY, decayX } = springRef.current
    const path = new Path2D()
    const { width, height } = _canvas
    
    setDisplayWidth(width)
    setDisplayHeight(height)
    const middleY = height / 2
    elasticity = rangeMap([1, 100], [.01, 1])(elasticity)
    gravity = rangeMap([1, 100], [.01, 2])(gravity)
    hang = rangeMap([0, 50], [1.8, 1])(hang)
    velocity = rangeMap([1, 100], [.01, 4])(velocity)
    decayX = rangeMap([100, 1], [.25, 1.25])(decayX)
    decayY = rangeMap([100, 1], [.01, .9])(decayY)
    let bounces: any[] = []
    let ognX = width/5
    let ognY = middleY
    let boxWidth = ((width / 20) * decayX) * elasticity * 2  * gravity
    let boxHeight = (height / 4) / elasticity * velocity
    let ognHandleX 
    let ognHandleY 
    let peakX = (boxWidth / 2) + ognX
    let peakY = middleY - boxHeight
    let peakHandleX  
    let peakHandleY
    
    for (let i = 0; i < elasticSegmentSearchMax; i++) {
      boxWidth *= decayX
      // Apply decayY only for bounces after the initial bounce
      if (i !== 0) {
        boxHeight *= decayY
      }
      decayX *= elasticity
      decayY /= elasticity

      if (i % 2 === 0) {
        ognHandleX = (boxWidth / 20) * hang + ognX  
        ognHandleY = middleY - (boxHeight / 2) 
        peakX = (boxWidth / 2) + ognX
        peakY = middleY - boxHeight  
        peakHandleX = (boxWidth / 4) * hang + ognX  
        peakHandleY = peakY 
        if(i === 0) {
          path.moveTo(ognX, ognY)
          path.bezierCurveTo(ognHandleX, ognHandleY, peakHandleX, peakHandleY, peakX, peakY)
          path.moveTo(peakX, peakY)
          let bounce = createHalfBounce({ognHandleX: ognHandleX, ognHandleY: ognHandleY}, 
          {ognX: ognX, ognY: ognY}, 
          {dstHandleX: peakHandleX, dstHandleY: peakHandleY}, 
          {dstX: peakX, dstY: peakY},
          {width: width, height: height}, 
          path)
          bounces.push(bounce)
        }

      path.moveTo(peakX, peakY + boxHeight/5)
      path.lineTo(peakX, peakY - boxHeight/5)
      } else {
      //find the valley x as half of the new box width plus the new ognx
      ognHandleX = (boxWidth / 20)  * hang + ognX  
      ognHandleY = middleY + (boxHeight / 2) / hang
      let valleyX = (boxWidth / 2) + ognX 
      let valleyY = middleY + boxHeight
      peakHandleX = (boxWidth / 4) * hang  + ognX  
      peakHandleY = peakY 

      let inverseTopHandleX = (ognX + boxWidth /2) - ((boxWidth / 4) * hang * 2)
      let inverseTopHandleY = peakHandleY
      let valleyHandleX =  (ognX + boxWidth/2) - ((boxWidth / 2) - ((boxWidth / 4) * hang)) * 2
      let valleyHandleY = valleyY

      if (Math.abs(valleyY - ognY) <= height * 0.002) { 
        path.moveTo(peakX, peakY)
        path.lineTo(width, ognY)
        break
      }
      path.moveTo(valleyX, valleyY)
      path.lineTo(valleyHandleX, valleyHandleY)
      path.moveTo(peakX, peakY)
      path.bezierCurveTo( inverseTopHandleX, inverseTopHandleY, valleyHandleX, valleyHandleY, valleyX, valleyY)
      path.moveTo(valleyX, valleyY)

      let bounce = createHalfBounce({ognHandleX: inverseTopHandleX, ognHandleY: inverseTopHandleY}, 
        {ognX: peakX, ognY: peakY}, 
        {dstHandleX: valleyHandleX, dstHandleY: valleyHandleY}, 
        {dstX: valleyX, dstY: valleyY},
        {width: width, height: height}, 
        path)
        bounces.push(bounce)

      let nextOgnX = boxWidth + ognX
      let valleyOutHandleX = (valleyX) + (boxWidth) - (boxWidth / 4) * hang * 2
      let valleyOutHandleY = valleyY
      path.moveTo(valleyX, valleyY)

      ognHandleX = (boxWidth / 20) * decayX  * hang + ognX  
      ognHandleY = middleY + (boxHeight * .5) / hang
      let nextPeakY = middleY - (boxHeight * decayY)
      let nextPeakX = nextOgnX + (boxWidth/2 * decayX)
      let nextPeakInHandleX = (nextPeakX) - (boxWidth) + (boxWidth / 4) * hang * 2
      path.bezierCurveTo( valleyOutHandleX, valleyOutHandleY, nextPeakInHandleX, nextPeakY, nextPeakX, nextPeakY) 

      bounce = createHalfBounce({ognHandleX: valleyOutHandleX, ognHandleY: valleyOutHandleY}, 
        {ognX: valleyX, ognY: valleyY}, 
        {dstHandleX: nextPeakInHandleX, dstHandleY: nextPeakY}, 
        {dstX: nextPeakX, dstY: nextPeakY},
        {width: width, height: height}, 
        path)
      bounces.push(bounce) 
      }
      ognX += boxWidth
    }

      props.setBounceValues(bounces)
      setNewBounces(bounces)
      // clear
      _ctx.clearRect(0, 0, width, height) 
      _ctx.globalAlpha = 1;

      // draw the dark line
      _ctx.strokeStyle = backgroundColor
      _ctx.lineJoin = 'round'
      _ctx.lineWidth = 14
      _ctx.stroke(path)

      // draw the white line
      _ctx.strokeStyle = accentColor
      _ctx.lineWidth = 2
      _ctx.stroke(path)
      gridLines(_canvas!, _ctx!)
      drawDashed(canvas.current!, ctx.current!)

    },
    [accentColor, backgroundColor, gradientTop, gradientBottom]
  )

    //@ts-ignore
    const gridLines = (_canvas: any, _ctx: any) => {
      let { width, height } = _canvas
      const rect = _canvas.getBoundingClientRect();
      const path = new Path2D()
      //multiplier to handle discrepancy between canvas size and display size
      let xMultiplier = width / rect.width
      let yMultiplier = height / rect.height
      let numXLines = Math.floor(rect.width / displayGridIncrement)
      let xRemainder = rect.width % displayGridIncrement * xMultiplier
      let xIncrementStretch = xRemainder ? xRemainder / numXLines : 0
      let numYLines = Math.floor(rect.height / displayGridIncrement)
      let yRemainder = rect.height % displayGridIncrement * yMultiplier
      let yIncrementStretch = yRemainder ? yRemainder / numYLines : 0
      _ctx.lineWidth = 1
      _ctx.globalAlpha = 0.16

      for (let i = 0; i <= numXLines; i++) {
        let x = i * (displayGridIncrement * xMultiplier) + xIncrementStretch * i
        path.moveTo(x, height)
        path.lineTo(x, 0)
      }
      for (let i = 0; i <= numYLines; i++) {
        let y = i * (displayGridIncrement * yMultiplier) + yIncrementStretch * i
        path.moveTo(width, y)
        path.lineTo(0, y)
      }
      _ctx.stroke(path)
    } 


  const [canvas, ctx] = useCanvas2d(drawSpring)
  function drawDashed(_canvas: any, _ctx: any) {
    const { width, height } = _canvas
      const path = new Path2D()
      _ctx.lineWidth = 3
      _ctx.setLineDash([6, 12]);
      path.moveTo(0, height)
      path.lineTo(width/5, height/2)
      _ctx.stroke(path)
      _ctx.setLineDash([]);
  }

  useEffect(() => {
      // @ts-ignore
    springRef.current = { elasticity, gravity, hang, velocity, decayY, decayX }
    drawSpring(canvas.current!, ctx.current!)
  }, [drawSpring,  elasticity, gravity, hang, velocity, decayY, decayX, canvas, ctx])

  return (
    <>
      <StyledBounce>
        <Canvas  ref={canvas} style={{  width: '100%', height: '100%', position: 'absolute', top: 0, left: 0,}} />
      </StyledBounce>
      <ElasticPreview bounceValues={newBounces} width={displayWidth} height={displayHeight}/>
    </>
  )
}